import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import html2pdf from 'html2pdf.js';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SignaturePadPublic } from '../components/SignaturePad';
import { IconDisplay } from '../components/IconPicker';
import { PreviewSignDocument } from '../components/PreviewSignDocument';
import { formsAPI } from '../utils/api';
import { toast } from 'sonner';
import { CheckCircle, Upload, X, Lock, ChevronLeft, ChevronRight, FileText, Image as ImageIcon, ShieldCheck, Check, Trophy, XCircle, PartyPopper, Download, RefreshCw, HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Confetti } from '../components/Confetti';
import { AddressField } from '../components/AddressField';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// Country list
const countries = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria", "Bangladesh", 
  "Belgium", "Brazil", "Canada", "Chile", "China", "Colombia", "Denmark", "Egypt", 
  "Finland", "France", "Germany", "Greece", "Hong Kong", "India", "Indonesia", "Iran", 
  "Iraq", "Ireland", "Israel", "Italy", "Japan", "Kenya", "Malaysia", "Mexico", 
  "Netherlands", "New Zealand", "Nigeria", "Norway", "Pakistan", "Peru", "Philippines", 
  "Poland", "Portugal", "Russia", "Saudi Arabia", "Singapore", "South Africa", "South Korea", 
  "Spain", "Sri Lanka", "Sweden", "Switzerland", "Taiwan", "Thailand", "Turkey", 
  "UAE", "UK", "Ukraine", "USA", "Vietnam"
];

export const PublicFormPage = () => {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Password protection
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordVerified, setPasswordVerified] = useState(false);
  
  // Multi-step
  const [currentStep, setCurrentStep] = useState(1);
  
  // Quiz results
  const [quizResult, setQuizResult] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);

  // Formlio Sign workflow
  const [showSignPreview, setShowSignPreview] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [signResult, setSignResult] = useState(null);
  const [documentNumber, setDocumentNumber] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);

  // Download filled form data as PDF (for submission confirmation).
  // Calls the backend ReportLab endpoint so the PDF uses the SAME premium
  // layout as signed documents and the text is vector / selectable.
  const handleDownloadFilledPDF = async () => {
    if (!submissionId) {
      toast.error('Submission reference missing — please refresh and try again.');
      return;
    }
    const apiFormId = form?.short_id || form?.id || formId;
    const url = `${API_URL}/api/forms/${apiFormId}/submissions/${submissionId}/pdf`;
    toast.info('Generating PDF...');
    try {
      const response = await fetch(url, { method: 'GET' });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to download PDF');
      }
      // Pull filename from Content-Disposition if present
      let filename = `${(form?.title || 'form').replace(/[^A-Za-z0-9._-]+/g, '_')}_${documentNumber || 'submission'}.pdf`;
      const cd = response.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      if (m && m[1]) filename = m[1];

      const blob = await response.blob();
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success('PDF downloaded successfully!');
    } catch (err) {
      toast.error(err?.message || 'Failed to generate PDF');
    }
  };

  // Legacy html2pdf renderer kept for reference (disabled). The backend
  // endpoint now produces a unified, text-selectable, premium-styled PDF.
  // eslint-disable-next-line no-unused-vars
  const _legacyHandleDownloadFilledPDF_unused = () => {
    // Create a styled HTML representation of the filled form
    const filledDataHTML = document.createElement('div');
    filledDataHTML.style.padding = '40px';
    filledDataHTML.style.fontFamily = form?.theme?.font_family || 'DM Sans, sans-serif';
    filledDataHTML.style.maxWidth = '800px';
    filledDataHTML.style.margin = '0 auto';
    
    // Header with business letterhead + form title
    let businessHeader = '';
    if (businessProfile) {
      const logoHtml = businessProfile.company_logo
        ? `<img src="${businessProfile.company_logo.startsWith('/api') ? API_URL + businessProfile.company_logo : businessProfile.company_logo}" style="width: 48px; height: 48px; object-fit: contain;" alt="" />`
        : '';
      const contactLines = [
        businessProfile.company_phone,
        businessProfile.company_email,
        businessProfile.company_address
      ].filter(Boolean).map(l => `<div style="font-size: 11px; color: #475569;">${l}</div>`).join('');

      businessHeader = `
        <div style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 15px; border-bottom: 2px solid #CBD5E1; margin-bottom: 20px;">
          <div style="display: flex; align-items: center; gap: 12px;">
            ${logoHtml}
            <span style="font-size: 18px; font-weight: 700; color: #1E293B;">${businessProfile.company_name}</span>
          </div>
          <div style="text-align: right; padding-left: 16px; border-left: 2px solid #CBD5E1;">
            ${contactLines}
          </div>
        </div>
      `;
    }

    const docNumStr = documentNumber ? `<p style="font-size: 13px; color: #6366F1; font-weight: 600; font-family: monospace; margin: 0 0 5px 0;">${documentNumber}</p>` : '';
    
    const header = `
      ${businessHeader}
      <div style="text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #E2E8F0;">
        <h1 style="font-size: 24px; color: #1E293B; margin: 0 0 10px 0;">${form?.title || 'Form Submission'}</h1>
        ${docNumStr}
        <p style="font-size: 14px; color: #64748B; margin: 0;">${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
      </div>
    `;
    
    // Build questions and answers
    let questionsHTML = '<div style="space-y: 20px;">';
    const visibleFields = (form?.fields || []).filter(f => 
      !['page_break', 'heading', 'divider', 'html', 'static_text'].includes(f.type) && isFieldVisible(f)
    );
    
    const accentColor = form?.theme?.accent_color || '#4F46E5';
    
    visibleFields.forEach((field, index) => {
      const answer = formData[field.id];
      let displayAnswer = '';
      
      // Format answer based on field type
      if (field.type === 'name' && answer) {
        displayAnswer = `${answer.first || ''} ${answer.middle || ''} ${answer.last || ''}`.trim();
      } else if (field.type === 'address' && answer) {
        displayAnswer = [answer.street, answer.city, answer.state, answer.zip, answer.country].filter(Boolean).join(', ');
      } else if (field.type === 'checkbox' && Array.isArray(answer)) {
        displayAnswer = answer.join(', ');
      } else if (field.type === 'image_choice' && answer) {
        const selectedOption = field.options?.find(o => o.value === answer);
        displayAnswer = selectedOption?.label || answer;
      } else if (field.type === 'signature' && answer) {
        // Render signature as image
        displayAnswer = `<img src="${answer}" style="max-width: 300px; max-height: 150px; border: 1px solid #E2E8F0; border-radius: 4px;" alt="Signature" />`;
      } else if (field.type === 'image' && answer) {
        // Render uploaded image
        displayAnswer = `<img src="${answer}" style="max-width: 300px; max-height: 200px; border-radius: 4px;" alt="Uploaded image" />`;
      } else if (field.type === 'file' && answer) {
        // Show file name for file uploads
        const fileName = typeof answer === 'string' ? answer.split('/').pop() : 'File uploaded';
        displayAnswer = `📎 ${fileName}`;
      } else if (typeof answer === 'object') {
        displayAnswer = JSON.stringify(answer);
      } else {
        displayAnswer = answer || '(No answer provided)';
      }
      
      questionsHTML += `
        <div style="margin-bottom: 20px; padding: 15px; background-color: #F8FAFC; border-radius: 8px; border-left: 4px solid ${accentColor};">
          <p style="font-size: 14px; color: #64748B; margin: 0 0 5px 0;">Question ${index + 1}</p>
          <p style="font-size: 16px; font-weight: 600; color: #1E293B; margin: 0 0 10px 0;">${field.label}${field.required ? ' *' : ''}</p>
          <div style="font-size: 15px; color: #334155; margin: 0; padding: 10px; background-color: white; border-radius: 4px;">${displayAnswer}</div>
        </div>
      `;
    });
    questionsHTML += '</div>';
    
    // Add quiz score if applicable
    let scoreHTML = '';
    if (form?.is_quiz && quizResult) {
      const { score } = quizResult;
      const bgColor = score?.passed ? '#ECFDF5' : '#FEF3C7';
      const textColor = score?.passed ? '#059669' : '#D97706';
      const resultText = score?.passed ? '✓ Passed!' : 'Quiz Complete';
      scoreHTML = `
        <div style="margin-top: 30px; padding: 20px; background-color: ${bgColor}; border-radius: 8px; text-align: center;">
          <h2 style="font-size: 20px; color: ${textColor}; margin: 0 0 10px 0;">
            ${resultText}
          </h2>
          <p style="font-size: 24px; font-weight: bold; color: #1E293B; margin: 0;">
            Score: ${score?.percentage || 0}%
          </p>
          <p style="font-size: 14px; color: #64748B; margin: 5px 0 0 0;">
            ${score?.earned_points || 0} out of ${score?.total_points || 0} points
          </p>
        </div>
      `;
    }
    
    // Footer
    const footer = `
      <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #E2E8F0; text-align: center;">
        <p style="font-size: 12px; color: #94A3B8; margin: 0;">Generated by Formlio</p>
      </div>
    `;
    
    filledDataHTML.innerHTML = header + questionsHTML + scoreHTML + footer;
    
    // Generate PDF
    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `${form?.title || 'form'}_submission.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: false
      },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    
    toast.info('Generating PDF...');
    html2pdf().set(opt).from(filledDataHTML).save().then(() => {
      toast.success('PDF downloaded successfully!');
    }).catch(() => {
      toast.error('Failed to generate PDF');
    });
  };

  // Handle submit again - reset form for new submission
  const handleSubmitAgain = () => {
    // Reset all form state
    setSubmitted(false);
    setQuizResult(null);
    setShowCelebration(false);
    setCurrentStep(1);
    setUploadedFiles({});
    
    // Reset form data to initial values
    const initialData = {};
    (form?.fields || []).forEach(field => {
      if (field.type === 'checkbox' || field.type === 'multiple') {
        initialData[field.id] = [];
      } else if (field.type === 'icon_choice' && field.icon_choice_multi_select) {
        initialData[field.id] = [];
      } else if (field.type === 'name') {
        initialData[field.id] = { first: '', last: '' };
      } else if (field.type === 'address') {
        initialData[field.id] = { street: '', city: '', state: '', zip: '', country: '' };
      } else if (['file', 'image'].includes(field.type)) {
        initialData[field.id] = null;
      } else {
        initialData[field.id] = '';
      }
    });
    setFormData(initialData);
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info('Form reset - you can submit again!');
  };

  useEffect(() => {
    loadForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  /*
   * Dynamically rewrite <title> + Open Graph meta tags so that when this form
   * URL is shared on WhatsApp / Slack / Discord (crawlers that execute JS),
   * the link preview shows the form's OWN brand (company name + logo) instead
   * of generic Formlio branding. Static crawlers (Twitter, LinkedIn) still
   * see the neutral fallback set in public/index.html.
   */
  useEffect(() => {
    if (!form) return;
    const company = form.company_name || '';
    const title = form.title || 'Form';
    const description = form.description || (company ? `Submit ${title} via ${company}` : title);
    const docTitle = company ? `${title} | ${company}` : title;
    const logoUrl = form.logo_url || '';
    const absoluteLogo = logoUrl
      ? (logoUrl.startsWith('http') ? logoUrl : `${window.location.origin}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`)
      : '';

    document.title = docTitle;

    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        // Selector looks like 'meta[property="og:title"]' — parse the attribute.
        const m = selector.match(/meta\[(\w+)="([^"]+)"\]/);
        if (m) el.setAttribute(m[1], m[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value || '');
    };

    setMeta('meta[property="og:title"]', 'content', docTitle);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', window.location.href);
    setMeta('meta[property="og:site_name"]', 'content', company || 'Formlio');
    setMeta('meta[name="twitter:title"]', 'content', docTitle);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="description"]', 'content', description);

    if (absoluteLogo) {
      setMeta('meta[property="og:image"]', 'content', absoluteLogo);
      setMeta('meta[property="og:image:secure_url"]', 'content', absoluteLogo);
      setMeta('meta[property="og:image:alt"]', 'content', `${company || title} logo`);
      setMeta('meta[name="twitter:image"]', 'content', absoluteLogo);
      setMeta('meta[name="twitter:card"]', 'content', 'summary');
    } else {
      // No logo — remove any leftover image tags so we don't fall back to
      // Formlio branding in the preview card.
      ['meta[property="og:image"]', 'meta[property="og:image:secure_url"]',
       'meta[property="og:image:alt"]', 'meta[name="twitter:image"]']
        .forEach((sel) => { const el = document.querySelector(sel); if (el) el.remove(); });
      setMeta('meta[name="twitter:card"]', 'content', 'summary');
    }
  }, [form]);

  const loadForm = async () => {
    try {
      const response = await formsAPI.getPublic(formId);
      setForm(response.data);
      
      // Use form's brand identity (from Design tab) instead of fetching business profile
      const formData = response.data;
      if (formData.logo_url || formData.company_name || formData.business_email || formData.business_phone || formData.business_address || formData.business_website) {
        setBusinessProfile({
          company_logo: formData.logo_url || '',
          company_name: formData.company_name || '',
          company_email: formData.business_email || '',
          company_phone: formData.business_phone || '',
          company_address: formData.business_address || '',
          company_website: formData.business_website || ''
        });
      }
      
      if (response.data.password_protected) {
        setNeedsPassword(true);
      }
      
      const initialData = {};
      response.data.fields.forEach(field => {
        if (field.type === 'icon_choice' && field.icon_choice_multi_select) {
          initialData[field.id] = [];
        } else if (field.type === 'name') {
          initialData[field.id] = { first: '', last: '' };
        } else if (field.type === 'address') {
          initialData[field.id] = { street: '', city: '', state: '', zip: '', country: '' };
        } else if (['file', 'image'].includes(field.type)) {
          initialData[field.id] = null;
        } else if (['select', 'radio'].includes(field.type)) {
          // Single-select: pre-select from ``default_value`` if set.
          initialData[field.id] = field.default_value || '';
        } else if (['checkbox', 'multiple'].includes(field.type)) {
          // Multi-select: pre-check from ``default_values`` array if set.
          initialData[field.id] = Array.isArray(field.default_values) ? [...field.default_values] : [];
        } else {
          // Pre-fill default_value for read-only fields.
          // If Default Value is blank but Placeholder is set, treat Placeholder as the
          // displayed content (forgiving UX — user often types content in Placeholder by mistake).
          if (field.read_only) {
            initialData[field.id] = field.default_value || field.placeholder || '';
          } else {
            initialData[field.id] = '';
          }
        }
      });
      setFormData(initialData);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Form not found');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldId, value) => {
    setFormData({ ...formData, [fieldId]: value });
  };

  const handleNameChange = (fieldId, part, value) => {
    setFormData({ 
      ...formData, 
      [fieldId]: { ...formData[fieldId], [part]: value } 
    });
  };

  const handleAddressChange = (fieldId, part, value) => {
    setFormData({ 
      ...formData, 
      [fieldId]: { ...formData[fieldId], [part]: value } 
    });
  };

  const handleCheckboxChange = (fieldId, option, checked) => {
    const current = formData[fieldId] || [];
    if (checked) {
      setFormData({ ...formData, [fieldId]: [...current, option] });
    } else {
      setFormData({ ...formData, [fieldId]: current.filter(o => o !== option) });
    }
  };

  const handleFileUpload = async (fieldId, file, field) => {
    if (!file) return;

    const maxSize = (field.max_file_size_mb || 10) * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Maximum size is ${field.max_file_size_mb || 10}MB`);
      return;
    }

    if (field.allowed_file_types && field.allowed_file_types.length > 0) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!field.allowed_file_types.includes(ext)) {
        toast.error(`Invalid file type. Allowed: ${field.allowed_file_types.join(', ')}`);
        return;
      }
    }

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await fetch(`${API_URL}/api/forms/${formId}/upload`, {
        method: 'POST',
        body: formDataUpload,
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const result = await response.json();
      setUploadedFiles({ ...uploadedFiles, [fieldId]: { ...result, previewUrl: URL.createObjectURL(file) } });
      setFormData({ ...formData, [fieldId]: result.file_id });
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    }
  };

  const removeFile = (fieldId) => {
    const newFiles = { ...uploadedFiles };
    delete newFiles[fieldId];
    setUploadedFiles(newFiles);
    setFormData({ ...formData, [fieldId]: null });
  };

  const isFieldVisible = (field) => {
    if (!field.condition) return true;
    const { field_id, operator, value } = field.condition;
    const fieldValue = formData[field_id];
    
    switch (operator) {
      case 'equals': return fieldValue === value;
      case 'not_equals': return fieldValue !== value;
      case 'contains': return String(fieldValue).toLowerCase().includes(value.toLowerCase());
      case 'greater_than': return Number(fieldValue) > Number(value);
      case 'less_than': return Number(fieldValue) < Number(value);
      default: return true;
    }
  };

  const getFieldsForStep = (step) => {
    if (!form?.enable_steps) return (form?.fields || []).filter(f => f.type !== 'page_break');
    return (form?.fields || []).filter(f => (f.step || 1) === step && f.type !== 'page_break');
  };

  const getTotalSteps = () => {
    if (!form?.enable_steps) return 1;
    // Count page breaks + 1 to determine total steps
    const pageBreaks = (form?.fields || []).filter(f => f.type === 'page_break').length;
    if (pageBreaks > 0) {
      return pageBreaks + 1;
    }
    // Fallback to step field values
    const steps = [...new Set((form?.fields || []).map(f => f.step || 1))];
    return Math.max(...steps, 1);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === form.form_password) {
      setPasswordVerified(true);
      setNeedsPassword(false);
    } else {
      toast.error('Incorrect password');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const visibleFields = form.fields.filter(isFieldVisible);
    for (const field of visibleFields) {
      if (field.required && !['heading', 'divider', 'html', 'page_break', 'static_text'].includes(field.type)) {
        const value = formData[field.id];
        
        // Special validation for name fields - both first and last are required
        if (field.type === 'name') {
          if (!value || !value.first || !value.last) {
            const missingPart = !value?.first && !value?.last ? 'First Name and Last Name' :
                               !value?.first ? 'First Name' : 'Last Name';
            toast.error(`${field.label}: ${missingPart} is required`);
            if (form.enable_steps) {
              setCurrentStep(field.step || 1);
            }
            return;
          }
          continue;
        }
        
        // Special validation for address fields - zip is optional, but if filled must match country format
        if (field.type === 'address') {
          if (!value || !value.street || !value.city || !value.state || !value.country) {
            toast.error(`${field.label}: Please fill street, city, state and country (postal code is optional)`);
            if (form.enable_steps) {
              setCurrentStep(field.step || 1);
            }
            return;
          }
          if (value.zip && value.zip.trim() && !/^[A-Za-z0-9 \-]{2,12}$/.test(value.zip.trim())) {
            toast.error(`${field.label}: Postal code format looks invalid. Please check.`);
            return;
          }
          continue;
        }
        
        // Standard validation for other fields
        if (!value || (Array.isArray(value) && value.length === 0) || 
            (typeof value === 'object' && !Array.isArray(value) && !Object.values(value).some(v => v))) {
          toast.error(`${field.label} is required`);
          if (form.enable_steps) {
            setCurrentStep(field.step || 1);
          }
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      const submissionData = {};
      form.fields.forEach(field => {
        if (isFieldVisible(field) && !['heading', 'divider', 'html', 'page_break', 'static_text'].includes(field.type)) {
          submissionData[field.label] = formData[field.id];
        }
      });
      
      // Check if this is a quiz
      if (form.is_quiz) {
        // Submit to quiz endpoint
        const response = await fetch(`${API_URL}/api/forms/${formId}/quiz-submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            data: submissionData,
            form_password: passwordInput || undefined
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to submit quiz');
        }
        
        const result = await response.json();
        setQuizResult(result);
        
        // Show celebration if passed OR scored 100%
        const scorePercentage = result.score?.percentage || 0;
        if ((result.score?.passed && form.passing_score) || scorePercentage >= 100) {
          setShowCelebration(true);
        }
        
        setSubmitted(true);
        toast.success('Quiz submitted successfully!');
      } else {
        // Regular form submission
        const response = await formsAPI.submit(formId, { 
          data: submissionData,
          form_password: passwordInput || undefined
        });
        
        // Capture document number from response
        const docNum = response.data?.document_number;
        if (docNum) setDocumentNumber(docNum);
        
        // Check if form has a signature field -> trigger signing workflow
        const hasSignatureField = (form.fields || []).some(f => f.type === 'signature');
        const sid = response.data?.submission_id;
        
        if (hasSignatureField && sid) {
          setSubmissionId(sid);
          setShowSignPreview(true);
          // Don't set submitted yet - wait for signing
        } else {
          if (sid) setSubmissionId(sid);
          setSubmitted(true);
          toast.success('Form submitted successfully!');
        }
      }
    } catch (error) {
      toast.error(error.message || error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const goToNextStep = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const fieldsInCurrentStep = getFieldsForStep(currentStep).filter(isFieldVisible);
    
    for (const field of fieldsInCurrentStep) {
      if (field.required && !['heading', 'divider', 'html', 'static_text'].includes(field.type)) {
        const value = formData[field.id];
        
        // Special validation for name fields
        if (field.type === 'name') {
          if (!value || !value.first || !value.last) {
            const missingPart = !value?.first && !value?.last ? 'First Name and Last Name' :
                               !value?.first ? 'First Name' : 'Last Name';
            toast.error(`${field.label}: ${missingPart} is required`);
            return;
          }
          continue;
        }
        
        // Special validation for address fields - zip is optional
        if (field.type === 'address') {
          if (!value || !value.street || !value.city || !value.state || !value.country) {
            toast.error(`${field.label}: Please fill street, city, state and country (postal code is optional)`);
            return;
          }
          continue;
        }
        
        // Standard validation
        if (!value || (Array.isArray(value) && value.length === 0)) {
          toast.error(`${field.label} is required`);
          return;
        }
      }
    }
    
    setCurrentStep(prev => Math.min(prev + 1, getTotalSteps()));
  };

  const goToPrevStep = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Render label with optional hover-text tooltip (JotForm-style help icon)
  const renderFieldLabel = (field, className) => {
    const hoverText = (field.hover_text || '').trim();
    return (
      <Label className={className}>
        <span className="inline-flex items-center gap-1.5">
          <span>{field.label}</span>
          {field.required && <span className="text-red-500">*</span>}
          {hoverText && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Help: ${field.label}`}
                    className="inline-flex items-center justify-center text-slate-400 hover:text-indigo-600 transition-colors cursor-help"
                    data-testid={`field-help-${field.id}`}
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs whitespace-pre-wrap">
                  {hoverText}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>
      </Label>
    );
  };

  // Render field based on type
  const renderField = (field, index) => {
    const commonLabelClass = "block text-sm font-medium text-slate-700 mb-1 mt-1";
    const commonInputClass = `w-full h-12 px-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${field.read_only ? 'bg-slate-100 cursor-not-allowed text-slate-600' : 'bg-white'}`;
    
    switch (field.type) {
      case 'heading':
        const HeadingTag = field.heading_size || 'h2';
        return (
          <div key={field.id} className="pt-6 pb-2">
            <HeadingTag className="text-xl font-semibold text-slate-800">{field.label}</HeadingTag>
          </div>
        );
        
      case 'divider':
        return <hr key={field.id} className="my-6 border-slate-200" />;
        
      case 'html':
        return (
          <div key={field.id} className="prose prose-sm max-w-none" 
               dangerouslySetInnerHTML={{ __html: field.html_content || '' }} />
        );

      case 'static_text':
        return (
          <div key={field.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm font-medium text-slate-700 mb-2">{field.label}</p>
            <p className="text-sm text-slate-600 whitespace-pre-wrap">{field.static_content || ''}</p>
          </div>
        );

      case 'name':
        return (
          <div key={field.id} className="space-y-2 pt-2">
            {renderFieldLabel(field, commonLabelClass)}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  placeholder={(field.sub_placeholders || {}).first_name || ''}
                  value={formData[field.id]?.first || ''}
                  onChange={(e) => handleNameChange(field.id, 'first', e.target.value)}
                  className={commonInputClass}
                />
                <p className="text-xs text-slate-500 mt-1">{(field.sublabels || {}).first_name || 'First Name'}</p>
              </div>
              <div>
                <Input
                  placeholder={(field.sub_placeholders || {}).last_name || ''}
                  value={formData[field.id]?.last || ''}
                  onChange={(e) => handleNameChange(field.id, 'last', e.target.value)}
                  className={commonInputClass}
                />
                <p className="text-xs text-slate-500 mt-1">{(field.sublabels || {}).last_name || 'Last Name'}</p>
              </div>
            </div>
          </div>
        );

      case 'address':
        return (
          <AddressField
            key={field.id}
            field={field}
            value={formData[field.id]}
            onChange={(addr) => setFormData({ ...formData, [field.id]: addr })}
            commonInputClass={commonInputClass}
            LabelComponent={renderFieldLabel(field, commonLabelClass)}
          />
        );

      case 'country':
        return (
          <div key={field.id} className="space-y-2 pt-2">
            {renderFieldLabel(field, commonLabelClass)}
            <Select
              value={formData[field.id] || ''}
              onValueChange={(value) => handleInputChange(field.id, value)}
            >
              <SelectTrigger className={commonInputClass}>
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        );

      case 'text':
      case 'email':
      case 'phone':
      case 'number':
      case 'url':
        return (
          <div key={field.id} className="space-y-2 pt-2">
            {renderFieldLabel(field, commonLabelClass)}
            {/* When read-only AND value has line breaks, render as static auto-expanding block (no scrollbar) */}
            {field.read_only && (formData[field.id] || '').includes('\n') ? (
              <div
                className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-slate-100 text-slate-700 text-sm whitespace-pre-wrap break-words"
                data-testid={`readonly-block-${field.id}`}
              >
                {formData[field.id]}
              </div>
            ) : (
              <Input
                type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : field.type === 'number' ? 'number' : field.type === 'url' ? 'url' : 'text'}
                placeholder={field.placeholder || ''}
                value={formData[field.id] || ''}
                onChange={(e) => !field.read_only && handleInputChange(field.id, e.target.value)}
                readOnly={field.read_only || false}
                className={commonInputClass}
              />
            )}
          </div>
        );

      case 'textarea':
        // When read-only, render an auto-expanding div so all lines are visible without a scrollbar
        if (field.read_only) {
          return (
            <div key={field.id} className="space-y-2 pt-2">
              {renderFieldLabel(field, commonLabelClass)}
              <div
                className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-slate-100 text-slate-700 text-sm whitespace-pre-wrap break-words"
                data-testid={`readonly-block-${field.id}`}
              >
                {formData[field.id] || field.default_value || ''}
              </div>
            </div>
          );
        }
        return (
          <div key={field.id} className="space-y-2 pt-2">
            {renderFieldLabel(field, commonLabelClass)}
            <Textarea
              placeholder={field.placeholder || ''}
              value={formData[field.id] || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all resize-y whitespace-pre-wrap bg-white"
            />
          </div>
        );

      case 'date':
      case 'time':
      case 'datetime': {
        const dateFormat = field.date_format || 'DD/MM/YYYY';
        const placeholder = field.type === 'time' ? 'HH:MM' : dateFormat;
        // Disable past dates so customers can't accidentally pick a backdated value
        const todayISO = new Date().toISOString().split('T')[0];
        const minAttr =
          field.type === 'date' ? { min: todayISO } :
          field.type === 'datetime' ? { min: `${todayISO}T00:00` } : {};
        return (
          <div key={field.id} className="space-y-2 pt-2">
            {renderFieldLabel(field, commonLabelClass)}
            <Input
              type={field.type === 'datetime' ? 'datetime-local' : field.type}
              value={formData[field.id] || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              placeholder={placeholder}
              className={commonInputClass}
              data-testid={`date-field-${field.id}`}
              {...minAttr}
            />
            {field.type !== 'time' && (
              <p className="text-xs text-slate-400">Format: {dateFormat} · Past dates disabled</p>
            )}
          </div>
        );
      }

      case 'select': {
        const selectedVal = formData[field.id] || '';
        const isOther = typeof selectedVal === 'string' && selectedVal.toLowerCase() === 'other';
        return (
          <div key={field.id} className="space-y-2 pt-2">
            {renderFieldLabel(field, commonLabelClass)}
            <Select
              value={selectedVal}
              onValueChange={(value) => handleInputChange(field.id, value)}
            >
              <SelectTrigger className={commonInputClass}>
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                {(field.options || []).map((option, idx) => (
                  <SelectItem key={idx} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isOther && (
              <Input
                placeholder="Please specify..."
                value={formData[`${field.id}_other`] || ''}
                onChange={(e) => handleInputChange(`${field.id}_other`, e.target.value)}
                className={`${commonInputClass} mt-2`}
                data-testid={`field-other-${field.id}`}
              />
            )}
          </div>
        );
      }

      case 'radio': {
        const selectedRadio = formData[field.id] || '';
        const isOther = typeof selectedRadio === 'string' && selectedRadio.toLowerCase() === 'other';
        return (
          <div key={field.id} className="space-y-2 pt-2">
            {renderFieldLabel(field, commonLabelClass)}
            <RadioGroup
              value={selectedRadio}
              onValueChange={(value) => handleInputChange(field.id, value)}
              className="space-y-2 pt-2"
            >
              {(field.options || []).map((option, idx) => (
                <div key={idx} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer">
                  <RadioGroupItem value={option} id={`${field.id}-${idx}`} />
                  <Label htmlFor={`${field.id}-${idx}`} className="font-normal cursor-pointer flex-1">{option}</Label>
                </div>
              ))}
            </RadioGroup>
            {isOther && (
              <Input
                placeholder="Please specify..."
                value={formData[`${field.id}_other`] || ''}
                onChange={(e) => handleInputChange(`${field.id}_other`, e.target.value)}
                className={`${commonInputClass} mt-2`}
                data-testid={`field-other-${field.id}`}
              />
            )}
          </div>
        );
      }

      case 'checkbox':
      case 'multiple': {
        const selectedArr = formData[field.id] || [];
        const isOther = Array.isArray(selectedArr) && selectedArr.some((v) => typeof v === 'string' && v.toLowerCase() === 'other');
        return (
          <div key={field.id} className="space-y-2 pt-2">
            {renderFieldLabel(field, commonLabelClass)}
            <div className="space-y-2 pt-2">
              {(field.options || []).map((option, idx) => (
                <div key={idx} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer">
                  <Checkbox
                    id={`${field.id}-${idx}`}
                    checked={selectedArr.includes(option)}
                    onCheckedChange={(checked) => handleCheckboxChange(field.id, option, checked)}
                  />
                  <Label htmlFor={`${field.id}-${idx}`} className="font-normal cursor-pointer flex-1">{option}</Label>
                </div>
              ))}
            </div>
            {isOther && (
              <Input
                placeholder="Please specify..."
                value={formData[`${field.id}_other`] || ''}
                onChange={(e) => handleInputChange(`${field.id}_other`, e.target.value)}
                className={`${commonInputClass} mt-2`}
                data-testid={`field-other-${field.id}`}
              />
            )}
          </div>
        );
      }

      case 'image_choice':
        return (
          <div key={field.id} className="space-y-2 pt-2">
            {renderFieldLabel(field, commonLabelClass)}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(field.image_options || []).map((option, idx) => {
                const isSelected = formData[field.id] === option.label;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleInputChange(field.id, option.label)}
                    className={`relative group rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                      isSelected 
                        ? 'border-indigo-500 ring-2 ring-indigo-500/20 shadow-lg' 
                        : 'border-slate-200 hover:border-indigo-300 hover:shadow-md'
                    }`}
                  >
                    {option.image_url ? (
                      <img 
                        src={option.image_url} 
                        alt={option.label}
                        className="w-full aspect-square object-cover"
                      />
                    ) : (
                      <div className="w-full aspect-square bg-slate-100 flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                    <div className={`absolute inset-0 flex items-end justify-center pb-2 bg-gradient-to-t from-black/60 to-transparent ${
                      isSelected ? '' : 'opacity-0 group-hover:opacity-100'
                    } transition-opacity`}>
                      <span className="text-white text-sm font-medium px-2 text-center">{option.label}</span>
                    </div>
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 'icon_choice':
        const isIconMultiSelect = field.icon_choice_multi_select;
        return (
          <div key={field.id} className="space-y-2 pt-2">
            {renderFieldLabel(field, commonLabelClass)}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(field.icon_options || []).map((option, idx) => {
                const currentValue = formData[field.id];
                const isSelected = isIconMultiSelect
                  ? Array.isArray(currentValue) && currentValue.includes(option.label)
                  : currentValue === option.label;
                
                const handleClick = () => {
                  if (isIconMultiSelect) {
                    const current = Array.isArray(currentValue) ? currentValue : [];
                    if (isSelected) {
                      handleInputChange(field.id, current.filter(v => v !== option.label));
                    } else {
                      handleInputChange(field.id, [...current, option.label]);
                    }
                  } else {
                    handleInputChange(field.id, option.label);
                  }
                };

                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={handleClick}
                    className={`relative group p-5 rounded-2xl border-2 transition-all duration-200 text-center ${
                      isSelected 
                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500/20 shadow-lg scale-[1.02]' 
                        : 'border-slate-200 bg-white hover:border-indigo-300 hover:shadow-md hover:scale-[1.01]'
                    }`}
                  >
                    {/* Icon - larger for emojis */}
                    <div className="w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                      <IconDisplay icon={option.icon} className="h-12 w-12" />
                    </div>
                    
                    {/* Label */}
                    <h4 className={`font-semibold text-sm mb-1 ${isSelected ? 'text-indigo-900' : 'text-slate-800'}`}>
                      {option.label}
                    </h4>
                    
                    {/* Description */}
                    {option.description && (
                      <p className={`text-xs ${isSelected ? 'text-indigo-600' : 'text-slate-500'}`}>
                        {option.description}
                      </p>
                    )}

                    {/* Selected indicator */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {isIconMultiSelect === true && (
              <p className="text-xs text-slate-500">You can select multiple options</p>
            )}
          </div>
        );

      case 'file':
      case 'image':
        return (
          <div key={field.id} className="space-y-2 pt-2">
            {renderFieldLabel(field, commonLabelClass)}
            {!uploadedFiles[field.id] ? (
              <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50 hover:border-indigo-400 transition-all">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {field.type === 'image' ? (
                    <ImageIcon className="w-10 h-10 text-slate-400 mb-3" />
                  ) : (
                    <Upload className="w-10 h-10 text-slate-400 mb-3" />
                  )}
                  <p className="text-sm text-slate-600 font-medium">Click to upload {field.type === 'image' ? 'image' : 'file'}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Max {field.max_file_size_mb || 10}MB
                    {field.allowed_file_types?.length > 0 && ` (${field.allowed_file_types.join(', ')})`}
                  </p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => handleFileUpload(field.id, e.target.files[0], field)}
                  accept={field.type === 'image' ? 'image/*' : field.allowed_file_types?.map(t => `.${t}`).join(',')}
                />
              </label>
            ) : (
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex items-center space-x-4">
                  {field.type === 'image' && uploadedFiles[field.id].previewUrl ? (
                    <img src={uploadedFiles[field.id].previewUrl} alt="Preview" className="w-16 h-16 object-cover rounded-lg" />
                  ) : (
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-indigo-600" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-slate-700">{uploadedFiles[field.id].filename}</p>
                    <p className="text-xs text-slate-500">Uploaded successfully</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile(field.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            )}
          </div>
        );

      case 'signature':
        return (
          <div key={field.id} className="space-y-2 pt-2">
            {renderFieldLabel(field, commonLabelClass)}
            <SignaturePadPublic
              value={formData[field.id] || ''}
              onChange={(value) => handleInputChange(field.id, value)}
              size={field.signature_size || 'default'}
            />
          </div>
        );

      case 'captcha':
        return (
          <div key={field.id} className="space-y-2 pt-2">
            <Label className={commonLabelClass}>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-green-600" />
                {field.label || 'Spam Protection'}
              </div>
            </Label>
            {RECAPTCHA_SITE_KEY ? (
              <div className="flex justify-center">
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(token) => handleInputChange(field.id, token)}
                  onExpired={() => handleInputChange(field.id, '')}
                />
              </div>
            ) : (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-center">
                <p className="text-amber-700 text-sm">CAPTCHA not configured</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Form Not Found</h2>
          <p className="text-slate-600">This form may have been removed or is no longer available.</p>
        </div>
      </div>
    );
  }

  // Password protection screen
  if (needsPassword && !passwordVerified) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-6"
        style={{ 
          backgroundColor: form.theme?.background_color || '#F8FAFC',
          fontFamily: form.theme?.font_family || 'DM Sans'
        }}
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Protected Form</h2>
            <p className="text-slate-600 mt-2">Enter the password to access this form</p>
          </div>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <Input
              type="password"
              placeholder="Enter password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              className="h-12 rounded-xl"
            />
            <Button 
              type="submit" 
              className="w-full h-12 rounded-xl text-white"
              style={{ backgroundColor: form.theme?.accent_color || '#4F46E5' }}
            >
              Unlock Form
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Success screen
  if (submitted) {
    // Quiz result screen
    if (form.is_quiz && quizResult && form.show_results !== false) {
      const { score, question_results } = quizResult;
      const isPassed = score?.passed;
      
      return (
        <div 
          className="min-h-screen flex items-center justify-center p-6"
          style={{ 
            backgroundColor: form.theme?.background_color || '#F8FAFC',
            fontFamily: form.theme?.font_family || 'DM Sans'
          }}
        >
          {/* Celebration animation */}
          {showCelebration && (
            <Confetti 
              duration={5000} 
              onComplete={() => setShowCelebration(false)} 
            />
          )}
          
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 max-w-lg w-full" data-testid="quiz-result-card">
            {/* Score circle */}
            <div className="text-center mb-8">
              <div 
                className={`w-32 h-32 rounded-full flex flex-col items-center justify-center mx-auto mb-4 ${
                  isPassed ? 'bg-gradient-to-br from-emerald-400 to-emerald-600' : 'bg-gradient-to-br from-amber-400 to-orange-500'
                }`}
              >
                <span className="text-4xl font-bold text-white" data-testid="quiz-score-percentage">
                  {score?.percentage || 0}%
                </span>
                <span className="text-sm text-white/80">Score</span>
              </div>
              
              {/* Result message */}
              <div className="flex items-center justify-center gap-2 mb-2">
                {isPassed ? (
                  <>
                    <Trophy className="w-6 h-6 text-emerald-500" />
                    <h2 className="text-2xl font-bold text-emerald-600" data-testid="quiz-result-title">
                      Congratulations! You Passed!
                    </h2>
                  </>
                ) : (
                  <>
                    <PartyPopper className="w-6 h-6 text-amber-500" />
                    <h2 className="text-2xl font-bold text-slate-800" data-testid="quiz-result-title">
                      Quiz Complete!
                    </h2>
                  </>
                )}
              </div>
              
              <p className="text-slate-600" data-testid="quiz-score-details">
                You scored <strong>{score?.earned_points || 0}</strong> out of <strong>{score?.total_points || 0}</strong> points
              </p>
              
              {form.passing_score && (
                <p className="text-sm text-slate-500 mt-1">
                  Passing score: {form.passing_score}%
                </p>
              )}
            </div>
            
            {/* Question results (if showing correct answers) */}
            {question_results && question_results.length > 0 && (
              <div className="border-t pt-6 space-y-3" data-testid="quiz-question-results">
                <h3 className="font-semibold text-slate-800 mb-4">Question Results:</h3>
                {question_results.map((q, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg border ${
                      q.is_correct 
                        ? 'bg-emerald-50 border-emerald-200' 
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                        q.is_correct ? 'bg-emerald-500' : 'bg-red-500'
                      }`}>
                        {q.is_correct ? (
                          <Check className="w-4 h-4 text-white" />
                        ) : (
                          <X className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800 text-sm">{q.label}</p>
                        <p className="text-sm text-slate-600">
                          Your answer: <span className={q.is_correct ? 'text-emerald-600' : 'text-red-600'}>{q.user_answer || '(no answer)'}</span>
                        </p>
                        {!q.is_correct && q.correct_answer && (
                          <p className="text-sm text-emerald-600">
                            Correct answer: {q.correct_answer}
                          </p>
                        )}
                        <p className="text-xs text-slate-500 mt-1">
                          {q.points_earned}/{q.max_points} points
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Success message */}
            {form.success_message && (
              <div className="mt-6 p-4 bg-slate-50 rounded-lg text-center">
                <p className="text-slate-600">{form.success_message}</p>
              </div>
            )}
            
            {/* Download PDF Button */}
            <div className="mt-8 pt-6 border-t">
              <Button
                onClick={handleDownloadFilledPDF}
                className="w-full h-12 text-white font-semibold"
                style={{ backgroundColor: form.theme?.accent_color || '#4F46E5' }}
                data-testid="download-filled-pdf-btn"
              >
                <Download className="w-5 h-5 mr-2" />
                Download My Answers as PDF
              </Button>
            </div>
          </div>
        </div>
      );
    }
    
    // Regular form success screen
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-6"
        style={{ 
          backgroundColor: form.theme?.background_color || '#F8FAFC',
          fontFamily: form.theme?.font_family || 'DM Sans'
        }}
      >
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center max-w-md">
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: `${form.theme?.accent_color || '#4F46E5'}15` }}
          >
            <CheckCircle className="w-10 h-10" style={{ color: form.theme?.accent_color || '#4F46E5' }} />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-3" data-testid="thank-you-title">Thank You!</h2>
          <p className="text-slate-600 text-lg mb-4">{form.success_message || 'Your submission has been received.'}</p>
          
          {/* Document Number - shown for ALL submissions */}
          {(signResult?.document_number || documentNumber) && (
            <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-xl" data-testid="signed-doc-info">
              <div className="flex items-center justify-center gap-2 mb-2">
                <ShieldCheck className="w-5 h-5 text-indigo-600" />
                <span className="text-sm font-medium text-indigo-800">
                  {signResult ? 'Document Signed' : 'Document Number'}
                </span>
              </div>
              <p className="font-mono text-lg font-bold text-indigo-700 mb-1" data-testid="document-number">
                {signResult?.document_number || documentNumber}
              </p>
              <p className="text-xs text-indigo-600">Keep this number for your records</p>
            </div>
          )}

          {/* Download Signed PDF */}
          {signResult?.document_number && (
            <Button
              onClick={() => {
                const url = `${API_URL}/api/signed-documents/${signResult.document_number}/pdf`;
                window.open(url, '_blank');
              }}
              className="w-full h-12 text-white font-semibold mb-3"
              style={{ backgroundColor: form.theme?.accent_color || '#4F46E5' }}
              data-testid="download-signed-pdf-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Signed PDF
            </Button>
          )}

          {/* Download Answers PDF - only show when NOT signed */}
          {!signResult && (
            <Button
              onClick={handleDownloadFilledPDF}
              className="w-full h-12 text-white font-semibold mb-3"
              style={{ backgroundColor: form.theme?.accent_color || '#4F46E5' }}
              data-testid="download-filled-pdf-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              Download My Answers as PDF
            </Button>
          )}

          {/* Submit Again Button */}
          <Button
            onClick={handleSubmitAgain}
            variant="outline"
            className="w-full h-12 font-semibold border-2 mt-3"
            data-testid="submit-again-btn"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Submit Another Response
          </Button>

          {/* Formlio Sign branding */}
          {signResult && (
            <p className="text-xs text-indigo-600 font-medium mt-4">Powered by Formlio Sign</p>
          )}
        </div>
      </div>
    );
  }

  const totalSteps = getTotalSteps();
  const currentStepFields = getFieldsForStep(currentStep);
  const visibleFieldsInStep = currentStepFields.filter(isFieldVisible);
  
  // Debug logging
  console.log('DEBUG: currentStep:', currentStep, 'totalSteps:', totalSteps);
  console.log('DEBUG: currentStepFields:', currentStepFields.map(f => f.label));
  console.log('DEBUG: visibleFieldsInStep:', visibleFieldsInStep.map(f => f.label));

  // Theme-based button style
  const buttonStyle = {
    backgroundColor: form.theme?.accent_color || '#4F46E5',
    borderRadius: form.theme?.button_style === 'pill' ? '9999px' : form.theme?.button_style === 'square' ? '4px' : '12px'
  };

  return (
    <div 
      className="min-h-screen py-8 sm:py-12 px-4 sm:px-6"
      style={{ 
        backgroundColor: form.theme?.background_color || '#F8FAFC',
        fontFamily: form.theme?.font_family || 'DM Sans'
      }}
    >
      <div className="max-w-3xl mx-auto">
        {/* Form Card */}
        <div 
          className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200" 
          style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
        >
          {/* Brand Header - Full width light background */}
          {(form.logo_url || form.company_name) && (
            <div className="bg-gradient-to-b from-slate-100 to-slate-50 px-6 sm:px-10 py-6 border-b border-slate-200">
              <div className="flex items-center justify-center gap-3">
                {form.logo_url && (
                  <img src={form.logo_url} alt="Logo" className="h-12 sm:h-14 object-contain" />
                )}
                {form.company_name && (
                  <span className="text-xl font-bold text-slate-800">{form.company_name}</span>
                )}
              </div>
            </div>
          )}

          {/* Form Title & Description */}
          <div 
            className="px-6 sm:px-10 py-8 border-b"
            style={{ borderColor: `${form.theme?.accent_color || '#4F46E5'}20` }}
          >
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{form.title}</h1>
            {form.description && (
              <p className="text-slate-600 mt-3 text-base sm:text-lg leading-relaxed">{form.description}</p>
            )}
            
            {/* Step indicator */}
            {form.enable_steps && totalSteps > 1 && (
              <div className="mt-6">
                <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                  <span>Step {currentStep} of {totalSteps}</span>
                  <span>{Math.round((currentStep / totalSteps) * 100)}% complete</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${(currentStep / totalSteps) * 100}%`,
                      backgroundColor: form.theme?.accent_color || '#4F46E5'
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Form Body */}
          <div className="px-6 sm:px-10 py-8">
            <form onSubmit={handleSubmit}>
              <div className="flex flex-wrap gap-y-10 gap-x-4">
                {visibleFieldsInStep.map((field, index) => {
                  const w = field.width || 'full';
                  const widthStyle = w === 'half' ? { width: 'calc(50% - 8px)' } : w === 'third' ? { width: 'calc(33.333% - 11px)' } : { width: '100%' };
                  return (
                    <React.Fragment key={field.id}>
                      {field.new_row && w !== 'full' && <div style={{ width: '100%', height: 0 }} />}
                      <div style={widthStyle} className="min-w-0">
                        {renderField(field, index)}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Navigation buttons */}
              <div className="pt-6">
                {form.enable_steps && totalSteps > 1 ? (
                  <div className="flex items-center justify-between gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={(e) => goToPrevStep(e)}
                      disabled={currentStep === 1}
                      className="h-12 px-6 rounded-xl"
                    >
                      <ChevronLeft className="w-5 h-5 mr-2" />
                      Previous
                    </Button>
                    
                    {currentStep === totalSteps ? (
                      <Button
                        type="submit"
                        className="h-12 px-8 text-white font-semibold shadow-lg hover:shadow-xl transition-shadow"
                        style={buttonStyle}
                        disabled={submitting}
                      >
                        {submitting ? 'Submitting...' : form.submit_button_text}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={(e) => goToNextStep(e)}
                        className="h-12 px-8 text-white font-semibold"
                        style={buttonStyle}
                      >
                        Next
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </Button>
                    )}
                  </div>
                ) : (
                  <Button
                    type="submit"
                    className="w-full h-14 text-lg text-white font-semibold shadow-lg hover:shadow-xl transition-shadow"
                    style={buttonStyle}
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : form.submit_button_text}
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Branding - Always show unless Pro user explicitly disables it */}
        {(form.show_branding !== false || !form.can_remove_branding) && (
          <div className="text-center mt-8">
            <p className="text-sm text-slate-500">
              Powered by{' '}
              <a href="https://formlio.app" target="_blank" rel="noopener noreferrer" className="font-medium hover:underline" style={{ color: form.theme?.accent_color || '#4F46E5' }}>
                Formlio
              </a>
            </p>
          </div>
        )}
      </div>

      {/* Formlio Sign - Preview & Sign Modal (Mandatory - cannot be skipped) */}
      {showSignPreview && submissionId && (
        <PreviewSignDocument
          form={form}
          formData={formData}
          submissionId={submissionId}
          onComplete={(result) => {
            setSignResult(result);
            setShowSignPreview(false);
            setSubmitted(true);
          }}
        />
      )}
    </div>
  );
};
